/**
 * @fileoverview Painel "Resolver conversas" / "Excluir conversas" / "Excluir
 * contatos": filtros, prévia automática e o botão que abre a confirmação.
 *
 * A prévia recalcula a cada mudança de filtro (com debounce) e cancela a
 * requisição do filtro anterior; o botão só libera com a prévia dos filtros
 * atuais. A confirmação leva os filtros que o servidor devolveu (já validados)
 * e o retrato em que ele contou: o servidor reconta sobre o mesmo retrato,
 * então nunca resolve nem apaga mais do que esta tela mostrou.
 */

import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Loader2, RefreshCw, TriangleAlert } from "lucide-react";

import api from "../../services/api";
import useStyles from "./styles";
import { CheckMark, Stat } from "./parts";
import {
	PERIODS,
	errorCodeOf,
	filtersSummary,
	fmtNumber,
	fmtRangeEnd,
	fmtRangeStart,
	fmtTime,
	messageForCode,
	statusesOf,
	t,
	todayInSaoPaulo,
} from "./format";

// Textos e números de cada painel (chaves em connections.adminTools.cleanup.*).
const KIND_BY_OPERATION = { resolve_tickets: "resolve", delete_tickets: "tickets", delete_contacts: "contacts" };

const PREVIEW_DEBOUNCE_MS = 350;

// refreshSignal: muda quando o pai precisa de uma prévia nova (a da tela
// expirou ou os dados mudaram na confirmação).
const CleanupPanel = ({ operation, whatsAppId, refreshSignal, onConfirmRequest }) => {
	const classes = useStyles();
	const scope = KIND_BY_OPERATION[operation];
	const isResolve = operation === "resolve_tickets";
	const statusOptions = statusesOf(operation);
	// Conversas (resolver ou excluir): filtra por status e conta conversas.
	const isTickets = statusOptions.length > 0;
	const today = todayInSaoPaulo();

	const [statuses, setStatuses] = useState([]);
	const [includeGroups, setIncludeGroups] = useState(false);
	const [period, setPeriod] = useState("30d");
	const [dateFrom, setDateFrom] = useState("");
	const [dateTo, setDateTo] = useState("");
	const [preview, setPreview] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [refreshKey, setRefreshKey] = useState(0);
	const seq = useRef(0);

	const customValid =
		period !== "custom" || (!!dateFrom && !!dateTo && dateFrom <= dateTo && dateTo <= today);
	// Sem status marcado a prévia ainda roda para mostrar a contagem de cada
	// opção (o servidor devolve total zero e não varre as mensagens).
	const selectionEmpty = isTickets && statuses.length === 0;

	const filtersKey = JSON.stringify({
		statuses: statusOptions.filter((s) => statuses.includes(s)),
		includeGroups,
		period,
		dateFrom: period === "custom" ? dateFrom : null,
		dateTo: period === "custom" ? dateTo : null,
	});

	useEffect(() => () => {
		seq.current += 1; // desmontou: ignora respostas pendentes
	}, []);

	useEffect(() => {
		const current = ++seq.current;
		if (!customValid) {
			setPreview(null);
			setError(null);
			setLoading(false);
			return undefined;
		}
		setLoading(true);
		const source = axios.CancelToken.source();
		const timer = setTimeout(async () => {
			try {
				const { data } = await api.post(
					`/whatsapp/${whatsAppId}/admin-tools/preview`,
					{ operation, filters: JSON.parse(filtersKey) },
					{ cancelToken: source.token }
				);
				if (current !== seq.current) return;
				setPreview(data);
				setError(null);
			} catch (err) {
				if (axios.isCancel(err) || current !== seq.current) return;
				setPreview(null);
				setError(errorCodeOf(err) || "UNKNOWN");
			} finally {
				if (current === seq.current) setLoading(false);
			}
		}, PREVIEW_DEBOUNCE_MS);
		return () => {
			clearTimeout(timer);
			source.cancel(); // filtro mudou: a prévia anterior não interessa mais
		};
	}, [operation, whatsAppId, filtersKey, customValid, refreshKey, refreshSignal]);

	const counts = preview?.counts || {};
	const total = selectionEmpty ? 0 : Number(preview?.total || 0);
	const ready = !!preview && !loading && !error && customValid;

	const toggleStatus = (status) =>
		setStatuses((prev) => (prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]));

	const otherConnections = counts.otherConnections || [];
	const otherTickets = otherConnections.reduce((sum, c) => sum + (Number(c.tickets) || 0), 0);
	const messagesTotal = isTickets
		? counts.messages
		: (Number(counts.messagesInTickets) || 0) + (Number(counts.messagesInOtherChats) || 0);

	const openConfirm = () => {
		if (!ready || total === 0) return;
		// Resolver não apaga mensagens: sem a linha delas.
		const summary = isTickets
			? [
					t("confirm.summary.tickets", { n: fmtNumber(total), filters: filtersSummary(operation, preview.filters) }),
					...(isResolve
						? []
						: [t("confirm.summary.messages", { messages: fmtNumber(counts.messages), media: fmtNumber(counts.mediaFiles) })]),
					t("confirm.summary.previewAt", { time: fmtTime(preview.computedAt) }),
			  ]
			: [
					t("confirm.summary.contacts", {
						n: fmtNumber(total),
						shared: fmtNumber(counts.sharedContacts),
						filters: filtersSummary(operation, preview.filters),
					}),
					t("confirm.summary.contactTickets", { n: fmtNumber(counts.tickets), other: fmtNumber(otherTickets) }),
					t("confirm.summary.messages", { messages: fmtNumber(messagesTotal), media: fmtNumber(counts.mediaFiles) }),
					t("confirm.summary.previewAt", { time: fmtTime(preview.computedAt) }),
			  ];
		onConfirmRequest({
			mode: "create",
			operation,
			filters: preview.filters,
			snapshot: preview.snapshot,
			phrase: preview.phrase,
			summary,
		});
	};

	const renderStats = () => {
		if (!customValid) return <div className={classes.muted}>{t("cleanup.invalidDates")}</div>;
		if (error) {
			return (
				<div className={`${classes.callout} ${classes.calloutDanger}`} style={{ marginTop: 0 }}>
					<TriangleAlert size={16} />
					<span>{messageForCode(error)}</span>
				</div>
			);
		}
		if (!preview) return <div className={classes.muted}>{t("cleanup.calculating")}</div>;
		const statsClass = `${classes.stats} ${loading ? classes.statsLoading : ""}`;
		if (isResolve) {
			return (
				<div className={statsClass}>
					<Stat value={total} label={t("stats.tickets")} />
				</div>
			);
		}
		if (isTickets) {
			return (
				<div className={statsClass}>
					<Stat value={total} label={t("stats.tickets")} />
					<Stat value={selectionEmpty ? 0 : counts.messages} label={t("stats.messages")} />
					<Stat value={selectionEmpty ? 0 : counts.mediaFiles} label={t("stats.media")} />
				</div>
			);
		}
		const shared = Number(counts.sharedContacts) || 0;
		return (
			<div className={statsClass}>
				<Stat
					value={total}
					label={t("stats.contacts")}
					sub={t("stats.contactsSplit", { exclusive: fmtNumber(total - shared), shared: fmtNumber(shared) })}
				/>
				<Stat
					value={counts.tickets}
					label={t("stats.tickets")}
					sub={t("stats.ticketsHere", { n: fmtNumber(counts.ticketsThisConnection) })}
				/>
				<Stat
					value={messagesTotal}
					label={t("stats.messages")}
					sub={
						Number(counts.messagesInOtherChats) > 0
							? t("stats.messagesInGroups", { n: fmtNumber(counts.messagesInOtherChats) })
							: null
					}
				/>
				<Stat value={counts.mediaFiles} label={t("stats.media")} />
			</div>
		);
	};

	const renderWarnings = () => {
		if (!ready || total === 0 || isResolve) return null;
		if (isTickets) {
			const live = statuses.filter((s) => s !== "closed");
			if (live.length === 0) return null;
			return (
				<div className={`${classes.callout} ${classes.calloutDanger}`}>
					<TriangleAlert size={16} />
					<span>{t("warnings.liveTickets")}</span>
				</div>
			);
		}
		const warnings = [];
		if (Number(counts.sharedContacts) > 0) {
			warnings.push(
				<div key="shared" className={`${classes.callout} ${classes.calloutDanger}`}>
					<TriangleAlert size={16} />
					<div>
						{t("warnings.sharedContacts", {
							shared: fmtNumber(counts.sharedContacts),
							tickets: fmtNumber(otherTickets),
						})}
						<ul>
							{otherConnections.map((c) => (
								<li key={c.whatsappId ?? "none"}>
									{t("warnings.otherConnection", {
										name: c.name || t("warnings.noConnection"),
										tickets: fmtNumber(c.tickets),
										openOrPending: fmtNumber(c.openOrPending),
									})}
								</li>
							))}
						</ul>
					</div>
				</div>
			);
		}
		if (Number(counts.openOrPendingThisConnection) > 0) {
			warnings.push(
				<div key="live" className={`${classes.callout} ${classes.calloutDanger}`}>
					<TriangleAlert size={16} />
					<span>{t("warnings.liveTicketsHere", { n: fmtNumber(counts.openOrPendingThisConnection) })}</span>
				</div>
			);
		}
		return warnings;
	};

	const groupCount = isTickets ? counts.groupTicketsAvailable : counts.groupContactsAvailable;

	return (
		<div>
			<p className={classes.intro}>{t(`cleanup.${scope}.intro`)}</p>

			{isTickets && (
				<div className={classes.section}>
					<div className={classes.sectionTitle}>{t("cleanup.tickets.statusTitle")}</div>
					<div className={classes.optionGrid} role="group" aria-label={t("cleanup.tickets.statusTitle")}>
						{statusOptions.map((status) => {
							const on = statuses.includes(status);
							return (
								<button
									key={status}
									type="button"
									role="checkbox"
									aria-checked={on}
									className={`${classes.option} ${on ? classes.optionSelected : ""}`}
									onClick={() => toggleStatus(status)}
								>
									<CheckMark on={on} />
									<span className={classes.optionLabel}>{t(`status.${status}`)}</span>
									<span className={classes.optionCount}>
										{preview && customValid ? fmtNumber(counts[status]) : "—"}
									</span>
								</button>
							);
						})}
					</div>
				</div>
			)}

			<div className={classes.section}>
				<div className={classes.sectionTitle}>{t(`cleanup.${scope}.periodTitle`)}</div>
				<div className={classes.segmented} role="radiogroup" aria-label={t(`cleanup.${scope}.periodTitle`)}>
					{PERIODS.map((p) => (
						<button
							key={p}
							type="button"
							role="radio"
							aria-checked={period === p}
							className={`${classes.segment} ${period === p ? classes.segmentActive : ""}`}
							onClick={() => setPeriod(p)}
						>
							{t(`period.${p}`)}
						</button>
					))}
				</div>
				{period === "custom" && (
					<div className={classes.dateRow}>
						<label className={classes.dateField}>
							<span className={classes.label}>{t("period.from")}</span>
							<input
								type="date"
								className={`${classes.input} ${dateFrom && dateTo && dateFrom > dateTo ? classes.inputError : ""}`}
								value={dateFrom}
								max={dateTo || today}
								onChange={(e) => setDateFrom(e.target.value)}
							/>
						</label>
						<label className={classes.dateField}>
							<span className={classes.label}>{t("period.to")}</span>
							<input
								type="date"
								className={`${classes.input} ${dateTo > today ? classes.inputError : ""}`}
								value={dateTo}
								min={dateFrom || undefined}
								max={today}
								onChange={(e) => setDateTo(e.target.value)}
							/>
						</label>
					</div>
				)}
				<div className={classes.hint}>{t(`cleanup.${scope}.periodHint`)}</div>
				<button
					type="button"
					role="checkbox"
					aria-checked={includeGroups}
					className={classes.toggleRow}
					onClick={() => setIncludeGroups((v) => !v)}
				>
					<CheckMark on={includeGroups} />
					<span>{t(`cleanup.${scope}.includeGroups`)}</span>
					{ready && !selectionEmpty && Number(groupCount) > 0 ? (
						<span className={classes.toggleCount}>{t("cleanup.groupsFound", { n: fmtNumber(groupCount) })}</span>
					) : null}
				</button>
			</div>

			<div className={classes.preview} aria-live="polite">
				<div className={classes.previewHead}>
					<span className={classes.previewTitle}>{t("cleanup.previewTitle")}</span>
					<span className={classes.previewMeta}>
						{loading ? (
							<>
								<Loader2 size={14} className={classes.spin} />
								{t("cleanup.calculating")}
							</>
						) : preview ? (
							t("cleanup.computedAt", { time: fmtTime(preview.computedAt) })
						) : null}
						<button
							type="button"
							className={classes.iconBtn}
							onClick={() => setRefreshKey((n) => n + 1)}
							disabled={loading || !customValid}
							title={t("cleanup.refresh")}
							aria-label={t("cleanup.refresh")}
						>
							<RefreshCw size={14} />
						</button>
					</span>
				</div>
				{renderStats()}
				{preview && customValid && !error && (
					<div className={classes.rangeNote}>
						{preview.rangeFrom
							? t("cleanup.range", { from: fmtRangeStart(preview.rangeFrom), to: fmtRangeEnd(preview.rangeTo) })
							: t("cleanup.rangeAll")}
						{selectionEmpty ? ` · ${t("cleanup.tickets.pickStatus")}` : ""}
					</div>
				)}
				{renderWarnings()}
			</div>

			<div className={classes.actionBar}>
				<span className={classes.actionNote}>{t(`cleanup.${scope}.note`)}</span>
				<button
					type="button"
					className={isResolve ? classes.btnInk : classes.btnDanger}
					disabled={!ready || total === 0}
					onClick={openConfirm}
				>
					{total > 0 ? t(`cleanup.${scope}.submit`, { count: total, n: fmtNumber(total) }) : t(`nav.${scope}`)}
				</button>
			</div>
		</div>
	);
};

export default CleanupPanel;
